/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import docApi from "api";
import { isValidPageIndex, getPageOrigHeight, getPageOrigWidth } from "selectors/pageSelectors";
import { getViewPortHeight, getViewPortWidth } from "selectors/documentViewSelectors";
import * as stores from "stores";
import { pagesInView, thumbnailsInView } from "selectors/pagedViewSelectors";
import { subscribeToPageLoader } from "services/PageLoaderService";
import { getCurrentPageIndex } from "selectors/pageNumber";
import { getIsCropMode, getAssetURI } from "selectors/documentSelectors";

export const getCropApis = () => {
  function cropWillBegin(pageIndex, token) {
    const currentPageIndex = pageIndex || getCurrentPageIndex(stores);
    stores.documentStore.setIsCropMode(true);
    stores.documentStore.setShouldShowPageLoader(true);
    stores.documentStore.setPagesGeometryViaMediaBox();
    const pageOrigHeight = getPageOrigHeight(stores, currentPageIndex);
    const pageOrigWidth = getPageOrigWidth(stores, currentPageIndex);
    const viewPortWidth = getViewPortWidth(stores);
    const viewPortHeight = getViewPortHeight(stores);
    const zoomWidth = viewPortWidth / (pageOrigWidth + 140);
    const zoomHeight = viewPortHeight / (pageOrigHeight + 70);
    stores.zoom.setPageZoomLevel(Math.min(zoomWidth, zoomHeight));

    let pagesInViewObj = pagesInView(stores);
    const thumbanilsInViewObj = thumbnailsInView(stores);
    stores.documentStore.reloadPages(pagesInViewObj);
    stores.documentStore.reloadThumbnails(thumbanilsInViewObj);
    stores.pagedViewState.goToPage(currentPageIndex + 1);
    subscribeToPageLoader();
    return docApi
      .cropWillBegin(getAssetURI(stores), token)
      .then(resp => resp.data)
      .finally(() => {
        stores.documentStore.setShouldShowPageLoader(false);
      });
  }

  function cropPages(startPageNum, endPageNum, cropMargins) {
    if (isValidPageIndex(stores, startPageNum - 1) && isValidPageIndex(stores, endPageNum - 1)) {
      return docApi.cropPages(startPageNum - 1, endPageNum - 1, cropMargins).then(({ data }) => data);
    }
    return Promise.reject("Page number is not valid");
  }

  async function cropDidEnd(startPageNum, endPageNum, token, domain, docHashSelector, useDocHashForThrottling) {
    if (!getIsCropMode(stores)) {
      return;
    }

    stores.documentStore.setIsCropMode(false);
    stores.documentStore.setShouldShowPageLoader(true);
    try {
      await stores.documentStore.reloadPagesAndGeometries(startPageNum, endPageNum, {
        refreshGeometry: true
      });
    } catch (err) {
      stores.documentStore.setPagesGeometryViaCropBox();
      const pagesInViewObj = pagesInView(stores);
      const thumbanilsInViewObj = thumbnailsInView(stores);
      stores.documentStore.reloadPages(pagesInViewObj);
      stores.documentStore.reloadThumbnails(thumbanilsInViewObj);
      console.error("Error while reloading pages and geometries", err);
    }
    subscribeToPageLoader();
    return docApi
      .cropDidEnd(token, domain, docHashSelector, useDocHashForThrottling)
      .then(({ data }) => data)
      .finally(() => {
        stores.documentStore.setShouldShowPageLoader(false);
      });
  }

  return {
    crop: {
      cropWillBegin,
      cropPages,
      cropDidEnd
    }
  };
};
